import React from 'react';
import ToolsContent from './ToolsContent';
import TasksContent from './TasksContent';

/**
 * UtilitySidebar — right-side panel hosting Tools and Tasks, opened from
 * the icons in the main nav header. Separate from the AI assistant sidebar
 * (which is now strictly the Context-Aware AI on the left).
 *
 * `view` is 'tools' | 'tasks', controlled by the parent (AppShell), which
 * also owns open/close. Both content components render as absolute-inset
 * overlays, so each is wrapped in a `relative flex-1` container.
 */

const ICON = {
  tools: (<svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM11 13a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"/></svg>),
  tasks: (<svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clipRule="evenodd" /></svg>),
  close: (<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>),
};

export default function UtilitySidebar({ view, onSelectView, onClose }) {
  return (
    <div className="h-full w-full flex flex-col bg-white">
      {/* Header: Tools / Tasks toggle + close */}
      <div className="flex-shrink-0 h-11 px-2 border-b border-gray-200 flex items-center justify-between bg-gray-100">
        <div className="flex items-center gap-1">
          <button
            onClick={() => onSelectView('tools')}
            className={`h-7 px-2 rounded flex items-center gap-1.5 text-xs font-medium transition-colors ${view === 'tools' ? 'bg-emerald-100 text-emerald-700' : 'text-gray-500 hover:text-gray-800 hover:bg-gray-200'}`}
          >
            {ICON.tools}<span>Tools</span>
          </button>
          <button
            onClick={() => onSelectView('tasks')}
            className={`h-7 px-2 rounded flex items-center gap-1.5 text-xs font-medium transition-colors ${view === 'tasks' ? 'bg-emerald-100 text-emerald-700' : 'text-gray-500 hover:text-gray-800 hover:bg-gray-200'}`}
          >
            {ICON.tasks}<span>Tasks</span>
          </button>
        </div>
        <button
          onClick={onClose}
          title="Close"
          aria-label="Close"
          className="w-7 h-7 rounded flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-200 transition-colors"
        >
          {ICON.close}
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 min-h-0 relative">
        {view === 'tasks' ? <TasksContent /> : <ToolsContent onClose={onClose} />}
      </div>
    </div>
  );
}
